<script setup lang="ts">
import { LogoutIcon, SettingsIcon } from '@/icons'
import { RouterLink, useRouter } from 'vue-router'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const profileBlobUrl = ref('')

// const profileImageUrl = computed(() => {
//   if (!authStore.user?.profileImage) return ''
//   return authStore.user.profileImage
// })

// Clean up on component unmount
onUnmounted(() => {
  if (profileBlobUrl.value) {
    URL.revokeObjectURL(profileBlobUrl.value)
  }
})

const router = useRouter()

const dropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const menuItems = [
  { href: '/manage-account/', icon: SettingsIcon, text: 'Manage Account' },
]

const getInitials = (): string => {
  const firstName = authStore.user?.first_name || '';
  const lastName = authStore.user?.last_name || '';

  if (!firstName && !lastName) return 'U';

  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';

  return `${firstInitial}${lastInitial}` || 'U';
};

const displayName = computed(() => {
  const firstName = authStore.user?.first_name || '';
  const lastName = authStore.user?.last_name || '';
  return `${firstName} ${lastName}`.trim() || 'User';
});

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value
}

const closeDropdown = () => {
  dropdownOpen.value = false
}

const signOut = async () => {
  console.log('Signing out...')

  try {
    await authStore.logout()
    localStorage.removeItem('user_role')
    sessionStorage.clear()
    console.log('✅ User signed out successfully')
    router.push('/')
  } catch (error) {
    console.error('❌ Error during sign out:', error)
    authStore.clearAuth?.()
    localStorage.removeItem('user_role')
    sessionStorage.clear()
    router.push('/')
  } finally {
    closeDropdown()
  }
}

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && event.target instanceof Node && !dropdownRef.value.contains(event.target)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="relative" ref="dropdownRef">
    <button
      class="flex items-center text-gray-700 dark:text-gray-400"
      @click.prevent="toggleDropdown"
      aria-label="User menu"
      :aria-controls="dropdownOpen ? 'user-dropdown' : undefined"
    >
      <span class="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full hover:text-dark-900 h-8.5 w-8.5 hover:bg-gray-100 hover:text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white">
        <!-- <img
          v-if="profileImageUrl"
          :src="profileImageUrl"
          alt="User profile"
          class="object-cover w-full h-full rounded-full"
        /> -->
        <span class="flex items-center justify-center w-full h-full text-sm font-medium text-gray-600 dark:text-gray-300">
          {{ getInitials() }}
        </span>
      </span>
    </button>

    <div
      v-if="dropdownOpen"
      id="user-dropdown"
      class="absolute right-0 mt-[5px] z-50 flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
      role="menu"
      aria-label="User dropdown menu"
    >
      <div v-if="authStore.user">
        <span class="block font-medium text-gray-700 text-theme-sm dark:text-gray-400">
          {{ displayName }}
        </span>
        <span class="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
          {{ authStore.user.email }}
        </span>
        <span class="mt-1 inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
          {{ authStore.user.userType?.toUpperCase() || 'USER' }}
        </span>
      </div>

      <ul class="flex flex-col gap-1 pt-4 pb-3 border-b border-gray-200 dark:border-gray-800">
        <li v-for="item in menuItems" :key="item.href" role="none">
          <router-link
            :to="item.href"
            role="menuitem"
            class="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            @click="closeDropdown"
          >
            <component
              :is="item.icon"
              class="w-6 h-6 text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300"
              aria-hidden="true"
            />
            {{ item.text }}
          </router-link>
        </li>
      </ul>

      <button
        @click="signOut"
        role="menuitem"
        class="flex items-center gap-3 px-3 py-2 mt-3 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
      >
        <div class="flex items-center gap-3">
          <LogoutIcon
            class="w-6 h-6"
            aria-hidden="true"
          />
          Sign out
        </div>
      </button>
    </div>
  </div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
